import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { SaveProjectStatusParams } from "../../../../../../../../api/services/project/model";
import { useSaveProjectStatusMutation } from "../../../../../../../../api/services/project/projectApis";
import { MuiModal } from "../../../../../../../../components";
import { STATUS_BADGE_TYPE } from "../../../../../../../../components/statusBadge/StatusBadge";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getParsedBEErrors,
  isNotEmpty,
  trimValue,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { ProjectStatusFormKey } from "../../../../../../modelsAndSchemas/Models";
import { changeProjectStatusProgramSchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { projectStatusChangeSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ChangeProjectStatusWrapper } from "./ChangeProjectStatus.style";
import { ChangStatusFields } from "./components/changeStatusFields/ChangeStatusFields";
import dayjs from "dayjs";
import { projectStatusSelector } from "../../../../../../../../redux/slices/project/projectSelector";
import { BE_ERROR_CODES } from "../../../../../../../../helpers/constants";
import { updateProjectStatus } from "../../../../../../../../redux/slices/project/projectSlice";

interface ChangeProjectStatusProps {
  pageId: string;
  open: boolean;
  setShowChangeStatusModal: (value: boolean) => void;
  projectId: string;
  refetchPrimaryDetails: () => void;
}

export const ChangeProjectStatus: FC<ChangeProjectStatusProps> = ({
  pageId,
  open,
  setShowChangeStatusModal,
  projectId,
  refetchPrimaryDetails,
}) => {
  const projectStatus = useSelector(projectStatusSelector);
  const dispatch = useDispatch();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { commonLabels, fetchLabel, fetchDropdownValues, fetchComponentTitle } =
    useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  // Mapping of statuses to their allowed dropdown values
  const statusOptionMap: Record<string, string[]> = {
    [STATUS_BADGE_TYPE.closed]: [STATUS_BADGE_TYPE.open],
    [STATUS_BADGE_TYPE.active]: [STATUS_BADGE_TYPE.closed],
    [STATUS_BADGE_TYPE.open]: [
      STATUS_BADGE_TYPE.active,
      STATUS_BADGE_TYPE.closed,
    ],
  };

  const {
    mutateAsync: saveProjectStatusApi,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
    error: saveApiError,
  } = useSaveProjectStatusMutation();

  const getLabel = (childKey: string) =>
    fetchLabel(projectStatusChangeSiteCoreKeys.header, childKey);

  const getErrorMessageFromAPI = (): string => {
    const errorObj = getParsedBEErrors(saveApiError);
    switch (errorObj?.[0]?.errorCode) {
      case BE_ERROR_CODES.PROJECT_ALL_CHILD_PROGRAM_STATUS_NOT_CLOSED:
        return getLabel(
          projectStatusChangeSiteCoreKeys.allChildProgramStatusNotClosedError,
        ) as string;
      case BE_ERROR_CODES.PROJECT_ALL_EXPENSE_NOT_BILLED:
        return getLabel(
          projectStatusChangeSiteCoreKeys.allExpenseNotBilledError,
        ) as string;
      default:
        return commonLabels.someThingWentWrongPleaseTryAgainLater as string;
    }
  };

  useEffect(() => {
    if (isSaveError) {
      const errorMessage = getErrorMessageFromAPI();
      dispatch(
        toggleToast({
          open: true,
          message: errorMessage,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSaveError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSavePending,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSavePending]);

  useEffect(() => {
    if (isSaveSuccess) {
      const statusId = getValues(ProjectStatusFormKey.status);
      refetchPrimaryDetails();
      setShowChangeStatusModal(false);
      dispatch(updateProjectStatus(statusId));
    }
  }, [isSaveSuccess]);

  const projectStatusDropdownOption = fetchDropdownValues(
    projectStatusChangeSiteCoreKeys.header,
    projectStatusChangeSiteCoreKeys.statusList,
  );

  const statusDropdownOptions = useMemo(() => {
    if (isNotEmpty(projectStatus) && isNotEmpty(projectStatusDropdownOption)) {
      return projectStatusDropdownOption.filter((option) =>
        statusOptionMap[projectStatus]?.includes(option.value as string),
      );
    } else return [];
  }, [projectStatus, projectStatusDropdownOption]);

  const today = dayjs().format(userCountryDateFormat ?? "");

  const defaultValues = {
    [ProjectStatusFormKey.status]: statusDropdownOptions[0]?.value as string,
    [ProjectStatusFormKey.date]: today,
    [ProjectStatusFormKey.notes]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(changeProjectStatusProgramSchema),
  });

  const {
    handleSubmit,
    getValues,
    formState: { isDirty },
  } = methods;

  const handleSaveProgramDetails = () => {
    const { notes, status: statusId, date } = getValues();
    const params: SaveProjectStatusParams = {
      projectId,
      statusId,
      date: date ? convertToUTCFormat(date, userCountryDateFormat) : "",
      comment: trimValue(notes),
    };
    saveProjectStatusApi(params);
  };

  const handleCancelOkClick = () => {
    setShowCancelModal(false);
    setShowChangeStatusModal(false);
  };

  const handleCancelChangeStatus = () => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      setShowChangeStatusModal(false);
    }
  };
  const handleSubmitFormData = () => {
    handleSubmit(handleSaveProgramDetails)();
  };

  return (
    <MuiModal
      onClose={() => setShowChangeStatusModal(false)}
      title={fetchComponentTitle(projectStatusChangeSiteCoreKeys.header)}
      open={open}
      primaryButtonTitle={commonLabels.save}
      secondaryButtonTitle={commonLabels.cancel}
      hasLargeContent
      onSecondaryButtonClick={handleCancelChangeStatus}
      onPrimaryButtonClick={handleSubmitFormData}
    >
      <ChangeProjectStatusWrapper>
        <FormProvider {...methods}>
          <ChangStatusFields
            projectStatusList={statusDropdownOptions}
            pageId={pageId}
            userCountryDateFormat={userCountryDateFormat}
          />
        </FormProvider>
        {showCancelModal && (
          <CancelFieldModal
            showModal={showCancelModal}
            handleModalClose={() => setShowCancelModal(false)}
            handlePrimaryButtonClick={handleCancelOkClick}
          />
        )}
      </ChangeProjectStatusWrapper>
    </MuiModal>
  );
};
